
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useBrandManager } from "@/hooks/useBrandManager";

interface BrainstormingIdea {
  id: string;
  name: string;
  affecting_metric: string[];
  description: string;
  submitted_by: string;
  sales_channel_id: string;
  created_at: string;
  growth_squad?: {
    name: string;
    alias: string;
    email: string;
    user_id: string | null;
  } | null;
  sales_channels?: {
    name: string;
  } | null;
}

export const useBrainstormingIdeas = (salesChannelId?: string) => {
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();
  const [ideas, setIdeas] = useState<BrainstormingIdea[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const fetchIdeas = async () => {
    if (!user || !currentBrandId) {
      setIdeas([]);
      return;
    }

    try {
      setIsLoading(true);
      
      let query = supabase
        .from('growth_ideas')
        .select(`
          *,
          growth_squad!fk_growth_ideas_submitted_by (
            name,
            alias,
            email,
            user_id
          ),
          sales_channels!fk_brainstorming_ideas_sales_channel (
            name
          )
        `)
        .eq('brand_id', currentBrandId)
        .order('created_at', { ascending: false });

      if (salesChannelId) {
        query = query.eq('sales_channel_id', salesChannelId);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Transform the data to handle the foreign key relationships
      const transformedData: BrainstormingIdea[] = (data || []).map(item => ({
        ...item,
        growth_squad: Array.isArray(item.growth_squad) ? item.growth_squad[0] : item.growth_squad,
        sales_channels: Array.isArray(item.sales_channels) ? item.sales_channels[0] : item.sales_channels
      }));
      
      setIdeas(transformedData);
    } catch (error: any) {
      console.error('Error fetching brainstorming ideas:', error);
      toast({
        title: "Error",
        description: "Failed to fetch brainstorming ideas",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createIdea = async (ideaData: { 
    name: string; 
    affecting_metric: string[]; 
    description: string; 
    submitted_by: string;
    sales_channel_id: string;
    brand_id: string;
  }): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('growth_ideas')
        .insert([{
          ...ideaData,
          task_status: 'Draft', // Default status for new brainstorming ideas
          methodology: 'growth_hacking' // Default methodology
        }]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Brainstorming idea created successfully",
      });

      await fetchIdeas();
      return true;
    } catch (error: any) {
      console.error('Error creating brainstorming idea:', error);
      toast({
        title: "Error",
        description: "Failed to create brainstorming idea",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateIdea = async (id: string, updates: { 
    name: string; 
    affecting_metric: string[]; 
    description: string; 
  }): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('growth_ideas')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Brainstorming idea updated successfully",
      });

      await fetchIdeas();
      return true;
    } catch (error: any) {
      console.error('Error updating brainstorming idea:', error);
      toast({
        title: "Error",
        description: "Failed to update brainstorming idea",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteIdea = async (id: string): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('growth_ideas')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Brainstorming idea deleted successfully",
      });

      await fetchIdeas();
      return true;
    } catch (error: any) {
      console.error('Error deleting brainstorming idea:', error);
      toast({
        title: "Error",
        description: "Failed to delete brainstorming idea",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchIdeas();
  }, [user, currentBrandId, salesChannelId]);

  return {
    ideas,
    isLoading,
    createIdea,
    updateIdea,
    deleteIdea,
    refetch: fetchIdeas,
  };
};
